import { useEffect, useState } from "react";

// Indonesian regions for the checkout address picker, served as static JSON
// from /public/wilayah and built by scripts/build-wilayah.mjs (Kemendagri codes
// and names, plus a postal code per kelurahan). Split one file per parent so
// the page fetches only the level the customer is on.

export interface Region {
  code: string;
  name: string;
}

export interface Postcode {
  code: string;
  /** The kelurahan/desa this postal code covers, which is how a customer recognises theirs. */
  villages: string[];
}

export interface District extends Region {
  postcodes: Postcode[];
}

// Region data does not change while the page is open, so each file is fetched
// once and every picker shares it.
const cache = new Map<string, Promise<unknown>>();

function fetchJson<T>(url: string): Promise<T> {
  let p = cache.get(url) as Promise<T> | undefined;
  if (!p) {
    p = fetch(url).then((res) => {
      if (!res.ok) throw new Error(`${url}: ${res.status}`);
      return res.json() as Promise<T>;
    });
    // A failed fetch is not cached, so trying again actually retries.
    p.catch(() => cache.delete(url));
    cache.set(url, p);
  }
  return p;
}

export const wilayahUrl = {
  provinces: () => "/wilayah/provinces.json",
  regencies: (provinceCode: string) => `/wilayah/regencies/${provinceCode}.json`,
  districts: (regencyCode: string) => `/wilayah/districts/${regencyCode}.json`,
};

/** Loads a region file, or nothing while url is null (the level above isn't chosen yet). */
export function useWilayah<T>(url: string | null): { data: T | null; loading: boolean; error: boolean } {
  const [state, setState] = useState<{ url: string | null; data: T | null; error: boolean }>({
    url: null,
    data: null,
    error: false,
  });

  useEffect(() => {
    if (!url) return;
    let live = true;
    fetchJson<T>(url).then(
      (data) => live && setState({ url, data, error: false }),
      () => live && setState({ url, data: null, error: true }),
    );
    return () => {
      live = false;
    };
  }, [url]);

  // State from a previous url is stale the moment url changes.
  const current = url !== null && state.url === url;
  return {
    data: current ? state.data : null,
    loading: url !== null && !current,
    error: current && state.error,
  };
}

/**
 * Case-insensitive match on every word typed, in any order: "kota selatan" and
 * "selatan jakarta" both find "Kota Administrasi Jakarta Selatan", which a plain
 * substring match would not.
 */
export function matchesQuery(label: string, query: string): boolean {
  const hay = label.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => hay.includes(word));
}
